import {Formik} from 'formik';
import {FormWithButtons} from '@flexinet/ui-components';

import {FormComponent} from './FormComponent';
import {RecordSchema} from './RecordSchema';
import {CreateDnsZoneInput} from '../types';
import {stage} from '../../../config/stage';

// FNP-1191 CRUD half — the CREATE form wrapper.
//
// ⚠️ NAVIGATION-FREE, per the drawer pattern. No `useNavigate` and no
// `useParams`: the form lives inside a drawer, and the drawer's `onClose` is
// what happens after a submit. A `navigate()` in here would fight the drawer.
//
// ⚠️ TYPED AS `CreateDnsZoneInput`, NOT `DnsZone`. The pattern doc's generic
// RecordForm takes the full entity because its modules edit one — this one
// cannot. There is no `updateDnsZone` on the schema, so a form typed as
// `DnsZone` would carry `id`, `provider`, `nameservers` and `appearsDelegated`
// fields that no mutation accepts, and the "strip before sending" step the doc
// describes would exist only to undo a type that was wrong to begin with.

export const RecordForm = (
    {
        record,
        handleSubmit,
        handleCancel,
    }: {
        record?: CreateDnsZoneInput,
        // Returns a promise so a failed create keeps the drawer open — see
        // drawers/FormDrawer. Formik also uses the returned promise to decide
        // when `isSubmitting` ends, so a void signature would drop the spinner
        // the instant the button was clicked.
        handleSubmit: (values: CreateDnsZoneInput) => Promise<unknown>,
        handleCancel?: () => void,
    },
) => {

    // RETURNED, not fire-and-forget. Formik awaits what onSubmit returns, which
    // is what keeps `isSubmitting` true for the length of the real request.
    const onSubmit = (values: CreateDnsZoneInput) => handleSubmit(values);

    const onCancel = () => {
        handleCancel?.();
    };

    let initialValues: CreateDnsZoneInput = {
        connectionId: '',
        name: '',
        comment: '',
    };

    if (record) {
        initialValues = Object.assign({}, initialValues, record);
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={RecordSchema}
            onSubmit={onSubmit}
        >
            {({
                  handleSubmit: formikHandleSubmit,
                  isSubmitting,
                  ...rest
              }) => (
                <FormWithButtons
                    handleSubmit={formikHandleSubmit}
                    isSubmitting={isSubmitting}
                    handleCancel={onCancel}
                    debug={false && stage === 'ide'}
                    {...rest}
                >
                    <FormComponent/>
                </FormWithButtons>
            )}
        </Formik>
    );
};
